import { getCurrentLocale } from '@leagueplatform/locales';
import type { ConfigEntityAuthAction } from '@web-config-app/core';

export const getAcceptLanguageHeader = () => {
  const userLocale = getCurrentLocale();
  return {
    headers: new Headers({
      'accept-language': userLocale,
    }),
  };
};

export const combineHeaders = (...fetchOptions: RequestInit[]) => {
  const result: { [key: string]: string } = {};

  fetchOptions.forEach((options) => {
    if (!options.headers) return;

    const headers: Headers = new Headers(options.headers);
    Array.from(headers.entries()).forEach(([key, value]) => {
      result[key] = value;
    });
  });

  return new Headers(result);
};

export const isFullyQualifiedLeagueApiUrl = (path: string) =>
  /^https:\/\/api(?:\.[a-zA-Z0-9-]+)*\.league\.(?:dev|com)\//.test(path);

export const getUrlFromPath = (path: string, apiUrl: string) =>
  isFullyQualifiedLeagueApiUrl(path)
    ? path
    : `${apiUrl}${path.replace(/^\/?/, '/')}`;

export type ApiErrorBody = {
  errors?: Array<{ detail: string }>;
  message?: string;
  [key: string]: unknown;
};

export const parseJsonSafe = async <T = unknown>(
  resp: Response,
): Promise<{ data?: T; error?: string }> => {
  try {
    const data = (await resp.json()) as T;
    return { data };
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
};

export const isJsonResponse = (resp: Response) => {
  const contentType = resp.headers.get('content-type') ?? '';
  return contentType.toLowerCase().includes('application/json');
};

export const responseHasReadableBody = (resp: Response) => {
  // HTTP 204 (No Content) and 205 (Reset Content) are defined to have no body.
  if (resp.status === 204 || resp.status === 205) return false;
  const contentLength = resp.headers.get('content-length');
  if (contentLength === '0') return false;
  return true;
};

export const getErrorDetail = (body?: ApiErrorBody) =>
  body?.errors?.[0]?.detail ?? body?.message;

/** Builds headers for config entity auth: X-League-Config-Entity-Type and X-League-Config-Entity-Action */
export const buildConfigEntityAuthHeaders = (
  domainEntityType: string,
  action: ConfigEntityAuthAction,
): Record<string, string> => ({
  'X-League-Config-Entity-Type': domainEntityType,
  'X-League-Config-Entity-Action': action,
});
